/**
 * /admin/refunds — raise and settle customer refunds.
 *
 * The same job as Django admin's RefundRequest screen, but built the
 * other way round. Django asks for a subscription id and an amount in
 * paise with nothing on screen to check either against; ₹2,999 typed as
 * 2999 instead of 299900 is a 100× error the form cannot catch.
 *
 * Here you start from the money: every captured payment, per customer,
 * with how much of it is still refundable. You pick the payment, and the
 * amount comes pre-filled in RUPEES. The backend converts to paise.
 *
 * No business logic lives on this page. Every button posts to
 * /api/admin/v2/refunds/…, which calls the same services_refund state
 * machine Django admin does — so the RefundEvent trail, the audit rows,
 * the credit note and the customer emails are identical either way.
 *
 * Backed by:
 *   GET  /api/admin/v2/refunds/candidates/
 *   GET  /api/admin/v2/refunds/
 *   POST /api/admin/v2/refunds/
 *   POST /api/admin/v2/refunds/<id>/<action>/
 */
import { useEffect, useMemo, useState } from "react";
import {
  RefreshCw, AlertTriangle, Loader2, Search, Undo2, ShieldAlert,
  ChevronDown, ChevronRight, CheckCircle2, XCircle, Clock, Send,
} from "lucide-react";
import {
  listRefundCandidates, listRefunds, createRefund, refundAction,
} from "../../api/owner.api";


const STATUS_STYLES = {
  REQUESTED:  { cls: "bg-amber-500/15 text-amber-300 border-amber-500/30",   icon: Clock },
  APPROVED:   { cls: "bg-blue-500/15 text-blue-300 border-blue-500/30",      icon: CheckCircle2 },
  PROCESSING: { cls: "bg-violet-500/15 text-violet-300 border-violet-500/30",icon: Send },
  COMPLETED:  { cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", icon: CheckCircle2 },
  DENIED:     { cls: "bg-slate-500/15 text-slate-300 border-slate-500/30",   icon: XCircle },
  FAILED:     { cls: "bg-rose-500/15 text-rose-300 border-rose-500/30",      icon: AlertTriangle },
};

const FILTERS = ["ALL", "REQUESTED", "APPROVED", "PROCESSING", "COMPLETED", "FAILED", "DENIED"];


function fmtInr(n) {
  if (n == null) return "—";
  return `₹${Number(n).toLocaleString("en-IN", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.DENIED;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${s.cls}`}>
      <Icon className="w-3 h-3" />
      {status}
    </span>
  );
}


export default function Refunds() {
  const [candidates, setCandidates] = useState([]);
  const [totals, setTotals] = useState(null);
  const [reasonCodes, setReasonCodes] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [expanded, setExpanded] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [draft, setDraft] = useState(null);   // refund being composed

  useEffect(() => {
    load();
    // `load` reads only `filter`, which is already the dependency here —
    // adding the function itself would re-fetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [c, r] = await Promise.all([
        listRefundCandidates(),
        listRefunds({ status: filter }),
      ]);
      setCandidates(c.data.candidates || []);
      setTotals(c.data.totals || null);
      setReasonCodes(c.data.reason_codes || []);
      setRefunds(r.data.refunds || []);
      setCounts(r.data.counts || {});
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }

  const visibleCandidates = useMemo(() => {
    if (!q.trim()) return candidates;
    const needle = q.trim().toLowerCase();
    return candidates.filter((c) =>
      [c.owner?.label, c.owner?.email, c.plan, c.cashfree_subscription_id]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle))
    );
  }, [candidates, q]);

  async function submitDraft() {
    if (!draft) return;
    setBusyId("draft");
    setError(null);
    try {
      await createRefund({
        subscription_id: draft.subscription_id,
        amount_inr: draft.amount_inr,
        reason_code: draft.reason_code,
        customer_facing_reason: draft.customer_facing_reason,
        internal_note: draft.internal_note,
        related_payment_event_id: draft.related_payment_event_id || null,
      });
      setNotice(
        `Refund raised for ${draft.ownerLabel}. It is REQUESTED — nothing has ` +
        `been sent to Cashfree yet. Use "Approve & process" below to send it.`
      );
      setDraft(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusyId(null);
    }
  }

  async function act(refund, action) {
    // Money leaves the account on approve-process. Make the operator say
    // so out loud — same reasoning as the confirm prompt on the Django
    // admin action.
    const confirmText = {
      "approve-process": `Send ₹${refund.amount_inr} back to ${refund.owner?.label} at Cashfree now?\n\nThis moves real money and cannot be undone from here.`,
      "process": `Send ₹${refund.amount_inr} to Cashfree now?`,
      "approve": `Approve ₹${refund.amount_inr} for ${refund.owner?.label}? (No money moves until you process it.)`,
      "deny": `Deny this refund? The customer sees the denial in their refund history.`,
    }[action];
    if (confirmText && !window.confirm(confirmText)) return;

    let reason = "";
    if (action === "deny") {
      reason = window.prompt("Reason for denial (shown to the customer):", "") || "";
    }

    setBusyId(refund.id);
    setError(null);
    setNotice(null);
    try {
      const res = await refundAction(refund.id, action, { reason });
      const next = res.data;
      if (next.status === "FAILED") {
        const ev = (next.timeline || []).slice(-1)[0];
        setError(
          `Cashfree rejected this refund: ${ev?.error_message || "see timeline"}. ` +
          `No money moved. Do not retry until the cause is understood.`
        );
      } else if (next.status === "PROCESSING") {
        setNotice(
          `Sent to Cashfree. It stays PROCESSING until their confirmation ` +
          `arrives — settlement to the customer takes 5–7 days. Don't re-run it.`
        );
      }
      await load();
    } catch (e) {
      const st = e?.response?.status;
      const detail = e?.response?.data?.detail || e.message;
      setError(
        st === 422 ? `Can't target this refund at Cashfree: ${detail}`
        : st === 409 ? `State machine refused: ${detail}`
        : st === 503 ? `${detail} — the refund is untouched, retry is safe.`
        : detail
      );
      await load();
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Undo2 className="w-5 h-5" /> Refunds
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Raise a refund against a real captured payment. Approving sends it
            to Cashfree; the credit note, wallet reversal and customer email
            fire automatically once Cashfree confirms.
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border-default text-sm hover:bg-bg-secondary"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </header>

      {error && (
        <div className="rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-200 p-3 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span className="whitespace-pre-wrap">{error}</span>
        </div>
      )}
      {notice && (
        <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 p-3 text-sm flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{notice}</span>
        </div>
      )}

      {/* ── Refundable money ─────────────────────────────────────────── */}
      <section className="rounded-lg border border-border-default bg-bg-primary">
        <div className="p-4 border-b border-border-default flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-medium">Refundable payments</h2>
            <p className="text-xs text-text-secondary mt-0.5">
              {totals
                ? `${totals.subscriptions} subscription(s) · ${fmtInr(totals.refundable_inr)} still refundable`
                : "—"}
              {" · "}the ₹1 mandate authorisation is excluded (Cashfree refunds it itself)
            </p>
          </div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-text-secondary" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search customer, plan, cf_sub…"
              className="pl-8 pr-3 py-1.5 rounded-md bg-bg-secondary border border-border-default text-sm w-64"
            />
          </div>
        </div>

        {loading && !candidates.length ? (
          <div className="p-10 text-center text-text-secondary text-sm">
            <Loader2 className="w-4 h-4 inline mr-2 animate-spin" /> Reading the ledger…
          </div>
        ) : !visibleCandidates.length ? (
          <div className="p-10 text-center text-text-secondary text-sm">
            No captured payments match.
          </div>
        ) : (
          <div className="divide-y divide-border-default">
            {visibleCandidates.map((c) => {
              const open = expanded === c.subscription_id;
              return (
                <div key={c.subscription_id}>
                  <button
                    onClick={() => setExpanded(open ? null : c.subscription_id)}
                    className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-bg-secondary"
                  >
                    {open ? <ChevronDown className="w-4 h-4 shrink-0" />
                          : <ChevronRight className="w-4 h-4 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate flex items-center gap-2">
                        {c.owner?.label}
                        {c.owner?.missing && (
                          <span title="Subscription owner_id points at no user row">
                            <ShieldAlert className="w-3.5 h-3.5 text-amber-400" />
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-text-secondary truncate">
                        {c.owner?.email || "no email"} · {c.plan || "no plan"} · {c.subscription_status}
                        {c.is_trial ? " · trial" : ""}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-medium">{fmtInr(c.refundable_inr)}</div>
                      <div className="text-[11px] text-text-secondary">
                        of {fmtInr(c.captured_inr)} captured
                      </div>
                    </div>
                  </button>

                  {open && (
                    <div className="px-4 pb-4 pl-11 space-y-2">
                      {c.payments.map((p) => (
                        <div
                          key={p.payment_event_id}
                          className="flex items-center gap-3 text-sm rounded-md border border-border-default px-3 py-2"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-medium">{fmtInr(p.amount_inr)}</div>
                            <div className="text-[11px] text-text-secondary truncate">
                              {fmtDate(p.captured_at)} · {p.rail === "subscription"
                                ? `mandate debit · cf_payment ${p.cf_payment_id || "—"}`
                                : `order ${p.order_id}`}
                            </div>
                          </div>
                          <button
                            disabled={c.refundable_paise <= 0}
                            onClick={() => setDraft({
                              subscription_id: c.subscription_id,
                              related_payment_event_id: p.payment_event_id,
                              ownerLabel: c.owner?.label,
                              maxInr: c.refundable_inr,
                              amount_inr: Math.min(p.amount_inr, c.refundable_inr),
                              reason_code: reasonCodes[0]?.value || "OTHER",
                              customer_facing_reason: "",
                              internal_note: "",
                            })}
                            className="px-3 py-1 rounded-md bg-button-primary text-white text-xs font-medium disabled:opacity-40"
                          >
                            Refund this
                          </button>
                        </div>
                      ))}
                      {c.refundable_paise <= 0 && (
                        <p className="text-[11px] text-text-secondary">
                          Fully refunded already — nothing left to return.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── Refund requests ──────────────────────────────────────────── */}
      <section className="rounded-lg border border-border-default bg-bg-primary">
        <div className="p-4 border-b border-border-default flex items-center gap-2 flex-wrap">
          <h2 className="font-medium mr-2">Refund requests</h2>
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                filter === f
                  ? "bg-button-primary text-white border-button-primary"
                  : "bg-bg-primary border-border-default text-text-secondary hover:text-text-primary"
              }`}
            >
              {f}{f !== "ALL" && counts[f] ? ` (${counts[f]})` : ""}
            </button>
          ))}
        </div>

        {!refunds.length ? (
          <div className="p-10 text-center text-text-secondary text-sm">
            No refunds {filter === "ALL" ? "yet" : `in ${filter}`}.
          </div>
        ) : (
          <div className="divide-y divide-border-default">
            {refunds.map((r) => (
              <div key={r.id} className="px-4 py-3 flex items-center gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">
                    {fmtInr(r.amount_inr)} · {r.owner?.label}
                  </div>
                  <div className="text-xs text-text-secondary truncate">
                    {fmtDate(r.created_at)} · {r.reason_code} · by {r.requested_by}
                    {r.provider_refund_id ? ` · ref ${r.provider_refund_id}` : ""}
                  </div>
                </div>
                <StatusBadge status={r.status} />
                <div className="flex items-center gap-2">
                  {r.status === "REQUESTED" && (
                    <>
                      <button
                        disabled={busyId === r.id}
                        onClick={() => act(r, "approve-process")}
                        className="px-3 py-1 rounded-md bg-button-primary text-white text-xs font-medium disabled:opacity-40"
                      >
                        {busyId === r.id ? "Working…" : "Approve & process"}
                      </button>
                      <button
                        disabled={busyId === r.id}
                        onClick={() => act(r, "deny")}
                        className="px-3 py-1 rounded-md border border-border-default text-xs disabled:opacity-40"
                      >
                        Deny
                      </button>
                    </>
                  )}
                  {r.status === "APPROVED" && (
                    <button
                      disabled={busyId === r.id}
                      onClick={() => act(r, "process")}
                      className="px-3 py-1 rounded-md bg-button-primary text-white text-xs font-medium disabled:opacity-40"
                    >
                      {busyId === r.id ? "Working…" : "Send to Cashfree"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Compose ──────────────────────────────────────────────────── */}
      {draft && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-bg-primary border border-border-default rounded-lg w-full max-w-lg p-5 space-y-4">
            <div>
              <h3 className="font-medium">Refund {draft.ownerLabel}</h3>
              <p className="text-xs text-text-secondary mt-1">
                Saved as REQUESTED. Nothing reaches Cashfree until you approve it.
              </p>
            </div>

            <label className="block">
              <span className="text-xs text-text-secondary">Amount (₹)</span>
              <input
                type="number" step="0.01" min="1" max={draft.maxInr}
                value={draft.amount_inr}
                onChange={(e) => setDraft({ ...draft, amount_inr: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-md bg-bg-secondary border border-border-default text-sm"
              />
              <span className="text-[11px] text-text-secondary">
                Rupees, not paise. Max {fmtInr(draft.maxInr)} on this subscription.
              </span>
            </label>

            <label className="block">
              <span className="text-xs text-text-secondary">Reason</span>
              <select
                value={draft.reason_code}
                onChange={(e) => setDraft({ ...draft, reason_code: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-md bg-bg-secondary border border-border-default text-sm"
              >
                {reasonCodes.map((rc) => (
                  <option key={rc.value} value={rc.value}>{rc.label}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="text-xs text-text-secondary">
                Message to the customer (they see this)
              </span>
              <textarea
                rows={2}
                value={draft.customer_facing_reason}
                onChange={(e) => setDraft({ ...draft, customer_facing_reason: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-md bg-bg-secondary border border-border-default text-sm"
              />
            </label>

            <label className="block">
              <span className="text-xs text-text-secondary">Internal note (never shown)</span>
              <textarea
                rows={2}
                value={draft.internal_note}
                onChange={(e) => setDraft({ ...draft, internal_note: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-md bg-bg-secondary border border-border-default text-sm"
              />
            </label>

            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setDraft(null)}
                className="px-3 py-1.5 rounded-md border border-border-default text-sm"
              >
                Cancel
              </button>
              <button
                disabled={busyId === "draft" || !draft.amount_inr}
                onClick={submitDraft}
                className="px-3 py-1.5 rounded-md bg-button-primary text-white text-sm font-medium disabled:opacity-40"
              >
                {busyId === "draft" ? "Saving…" : "Raise refund"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
